import {
  call,
  put,
  takeLatest,
  takeEvery
} from "redux-saga/effects";
import {
  normalize,
  schema
} from "normalizr";
import {
  push
} from "react-router-redux";
import {
  authorizedRequest,
  downloadFile,
  uploadFile,
  downloadXLSFile
} from "utils/api";
import {
  insertToState,
  insertFundAttribute,
  insertFundCategory
} from "utils/reducers";
import {
  API_FUNDS,
  API_DEALS
} from "constants/apiRoutes";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";

import {
  defaultReturn,
  makePayload,
  generateDefaultActions,
  generateSwitch,
  requestSaga,
  subKeySaga,
  fillDictionary
} from "../utils/helpers";

export const FUND_DEALS_FETCH_REQUEST = "FUND_DEALS_FETCH_REQUEST";
export const FUND_DEALS_FETCH_SUCCESS = "FUND_DEALS_FETCH_SUCCESS";
export const FUND_DEALS_FETCH_FAILURE = "FUND_DEALS_FETCH_FAILURE";

export const ALL_FUNDS_FETCH_REQUEST = "ALL_FUNDS_FETCH_REQUEST";
export const ALL_FUNDS_FETCH_SUCCESS = "ALL_FUNDS_FETCH_SUCCESS";
export const ALL_FUNDS_FETCH_FAILURE = "ALL_FUNDS_FETCH_FAILURE";

export const FUNDS_FETCH_REQUEST = "FUNDS_FETCH_REQUEST";
export const FUNDS_FETCH_SUCCESS = "FUNDS_FETCH_SUCCESS";
export const FUNDS_FETCH_FAILURE = "FUNDS_FETCH_FAILURE";

export const FUNDS_DOWNLOAD = generateDefaultActions("FUNDS_DOWNLOAD");

export const GET_FUND_INFO = generateDefaultActions("GET_FUND_INFO");

export const FUND_SINGLE_FETCH_REQUEST = "FUND_SINGLE_FETCH_REQUEST";
export const FUND_SINGLE_FETCH_SUCCESS = "FUND_SINGLE_FETCH_SUCCESS";
export const FUND_SINGLE_FETCH_FAILURE = "FUND_SINGLE_FETCH_FAILURE";

export const FUND_CREATE_REQUEST = "FUND_CREATE_REQUEST";
export const FUND_CREATE_SUCCESS = "FUND_CREATE_SUCCESS";
export const FUND_CREATE_FAILURE = "FUND_CREATE_FAILURE";

export const FUND_UPDATE_REQUEST = "FUND_UPDATE_REQUEST";
export const FUND_UPDATE_SUCCESS = "FUND_UPDATE_SUCCESS";
export const FUND_UPDATE_FAILURE = "FUND_UPDATE_FAILURE";

export const FUND_AVP_ATTRIBUTE_ADD = "FUND_AVP_ATTRIBUTE_ADD";
export const FUND_AVP_CATEGORY_ADD = "FUND_AVP_CATEGORY_ADD";

export const ADD_NOTE_TO_FUND = generateDefaultActions("ADD_NOTE_TO_FUND");
export const GET_FUND_NOTES = generateDefaultActions("GET_FUND_NOTES");
export const DELETE_FUND_NOTES = generateDefaultActions("DELETE_FUND_NOTES");

export const ADD_FILE_TO_FUND = generateDefaultActions("ADD_FILE_TO_FUND");
export const GET_FUND_FILES = generateDefaultActions("GET_FUND_FILES");
export const DELETE_FUND_FILE = generateDefaultActions("DELETE_FUND_FILE");

export const GET_FUND_METRICS = generateDefaultActions("GET_FUND_METRICS");

export const GET_AVAILABLE_COLLAB_GROUPS = generateDefaultActions("GET_AVAILABLE_COLLAB_GROUPS");

export const GET_FUND_NAME = generateDefaultActions("GET_FUND_NAME");
export const getFundName = payload => makePayload(GET_FUND_NAME.value, payload);
function* getFundNameSaga(payload) {
  if (payload.fundId !== "free") {
    const url = `/getFundName/${payload.fundId}`;
    const requestSaga = subKeySaga(payload.subKey);
    yield requestSaga(GET_FUND_NAME, url);
  }
}

export const FUND_KEY = "funds";
export const FUND_ENTITY = new schema.Entity(FUND_KEY, {});
export const FUND_SCHEMA = [FUND_ENTITY];

export const DEAL_KEY = "deals";
export const DEAL_ENTITY = new schema.Entity(DEAL_KEY, {});
export const DEAL_SCHEMA = [DEAL_ENTITY];

const initialState = {
  // would be better to capsule this (funds, count, order) into property 'list'
  funds: [],
  count: 0,
  order: [],
  all: [],
  deals: {
    active: {},
    closed: {}
  },
  fundNames: {},
  fundNotes: [],
  fundFiles: [],
  metricData: [],
  availableCollabGroups: [],
  refreshFunds: false
};

export default function fundReducer(state = initialState, action) {
  const {
    type,
    payload,
    subKey
  } = action;
  switch (type) {
    case FUNDS_FETCH_SUCCESS:
      return {
        ...state,
        count: payload.count,
        funds: payload.data.funds || {},
        orig: payload.orig,
        order: payload.order,
        all: payload.all ? payload.all : state.all
      };

    case FUND_SINGLE_FETCH_SUCCESS:
      return {
        ...state,
        [payload.id]: payload
      };
    case GET_AVAILABLE_COLLAB_GROUPS.success:
      return { ...state, availableCollabGroups: payload.results || [] };
    case GET_FUND_NAME.success:
      return fillDictionary(state, payload.data, "fundNames", subKey);
    case GET_FUND_INFO.success:
      payload.fund.isAuthorized = true;

      return fillDictionary(state, payload.fund, "fundInfos", subKey);

    case GET_FUND_INFO.failure:
      payload.fund = { isAuthorized: false };

      return fillDictionary(state, payload.fund, "fundInfos", subKey);

    case FUND_UPDATE_SUCCESS:
      return insertToState(
        state,
        Object.keys(payload.data.funds)[0],
        payload.data.funds[Object.keys(payload.data.funds)[0]]
      );

    case FUND_CREATE_SUCCESS:
      ToastSuccess("Save successful");

      return insertToState(state, payload.fund.id, payload.fund);

    case FUND_AVP_ATTRIBUTE_ADD:
      return insertFundAttribute(state, payload);

    case FUND_AVP_CATEGORY_ADD:
      return insertFundCategory(state, payload);

    case ALL_FUNDS_FETCH_SUCCESS:
      return {
        ...state,
        all: payload.data.funds || {}
      };
    case GET_FUND_NOTES.success:
    case DELETE_FUND_NOTES.success:
      return fillDictionary(state, payload.data, "fundNotes", subKey);
    case GET_FUND_METRICS.success:
      return defaultReturn(state, payload.data, "metricData");
    case FUND_DEALS_FETCH_SUCCESS:
      return {
        ...state,
        deals: {
          ...state.deals,
          [payload.status]: {
            count: payload.count,
            deals: payload.data.deals || {},
            order: payload.order
          }
        }
      };
    case GET_FUND_FILES.success:
      return fillDictionary(state, payload.data, "fundFiles", subKey);
    case REFRESH_FUNDS.on:
      return defaultReturn(state, true, "refreshFunds");
    case REFRESH_FUNDS.off:
      return defaultReturn(state, false, "refreshFunds");
    default:
      return state;
  }
}

/**
 * Get paginated funds
 * @return {Object} Action
 */
export function fetchFunds(payload) {
  return {
    type: FUNDS_FETCH_REQUEST,
    ...payload
  };
}

export const REFRESH_FUNDS = generateSwitch("REFRESH_FUNDS");
export const refreshFundsSwitchOn = () => makePayload(REFRESH_FUNDS.value, { value: true });
export const refreshFundsSwitchOff = () => makePayload(REFRESH_FUNDS.value, { value: false });
export const getAvailableCollabGroups = payload => makePayload(GET_AVAILABLE_COLLAB_GROUPS.value, payload);

function* refreshFundsSwitchSaga(payload) {
  if (payload.value) {
    yield put(REFRESH_FUNDS.turnOn(payload));
  } else {
    yield put(REFRESH_FUNDS.turnOff(payload));
  }
}

function* getAvailableCollabGroupsSaga(payload) {
  const url = "/getAvailableCollabGroups";
  yield requestSaga(GET_AVAILABLE_COLLAB_GROUPS, url, "POST", payload);
}

export const downloadFunds = payload => makePayload(FUNDS_DOWNLOAD.value, payload);

export function fundFetchSuccess({
  funds,
  count
}) {
  return {
    type: FUNDS_FETCH_SUCCESS,
    payload: {
      data: normalize(funds, FUND_SCHEMA).entities,
      count,
      all: funds,
      order: funds.map(fund => fund.id)
    }
  };
}

function fundFetchFailure(payload) {
  return {
    type: FUNDS_FETCH_FAILURE,
    payload
  };
}

/**
 * Get all funds
 */
export function fetchAllFunds(payload) {
  return {
    type: ALL_FUNDS_FETCH_REQUEST,
    ...payload
  };
}

export function allFundFetchSuccess({
  funds
}) {
  return {
    type: ALL_FUNDS_FETCH_SUCCESS,
    payload: {
      data: normalize(funds, FUND_SCHEMA).entities
    }
  };
}

function allFundFetchFailure(payload) {
  return {
    type: ALL_FUNDS_FETCH_FAILURE,
    payload
  };
}

/**
 * Get a single fund
 * @return {Object} Action
 */
export function fetchSingleFund(payload) {
  return {
    type: FUND_SINGLE_FETCH_REQUEST,
    payload
  };
}

export function fundSingleFetchSuccess({
  fund
}) {
  return {
    type: FUND_SINGLE_FETCH_SUCCESS,
    payload: fund
  };
}

function fundSingleFetchFailure(payload) {
  return {
    type: FUND_SINGLE_FETCH_FAILURE,
    payload
  };
}

export const getFundInfo = payload => makePayload(GET_FUND_INFO.value, payload);

// create
export function createFund(payload) {
  return {
    type: FUND_CREATE_REQUEST,
    payload
  };
}

export function fundCreateSuccess(payload) {
  return {
    type: FUND_CREATE_SUCCESS,
    payload
  };
}

function fundCreateFailure(payload) {
  return {
    type: FUND_CREATE_FAILURE,
    payload
  };
}

/**
 * Get a update fund
 * @return {Object} Action
 */
export function updateFund(payload) {
  return {
    type: FUND_UPDATE_REQUEST,
    payload
  };
}

export function fundUpdateSuccess({
  fund
}) {
  return {
    type: FUND_UPDATE_SUCCESS,
    payload: {
      data: normalize({
        fund
      }, FUND_SCHEMA).entities
    }
  };
}

function fundUpdateFailure(payload) {
  return {
    type: FUND_UPDATE_FAILURE,
    payload
  };
}

// Add Custom Category or Item
export function fundAddAttribute(payload) {
  return {
    type: FUND_AVP_ATTRIBUTE_ADD,
    payload
  };
}

export function fundAddCategory(payload) {
  return {
    type: FUND_AVP_CATEGORY_ADD,
    payload
  };
}

/**
 * Get paginated deals for fund
 */
export function fetchFundDeals(payload) {
  return {
    type: FUND_DEALS_FETCH_REQUEST,
    ...payload
  };
}

export function fundDealsFetchSuccess({
  deals,
  count
}, status) {
  return {
    type: FUND_DEALS_FETCH_SUCCESS,
    payload: {
      data: normalize(deals, DEAL_SCHEMA).entities,
      count,
      order: deals.map(deal => deal.id),
      status
    }
  };
}

function fundDealsFetchFailure(payload) {
  return {
    type: FUND_DEALS_FETCH_FAILURE,
    payload
  };
}

export const addFileToFund = (model, modelId, file) => ({
  type: ADD_FILE_TO_FUND.value,
  model,
  modelId,
  file,
  url: "/files"
});
export const getFundFiles = payload => makePayload(GET_FUND_FILES.value, payload);
export const deleteFundFile = payload => makePayload(DELETE_FUND_FILE.value, payload);

// Saga
function* fetchPaginatedSaga(payload) {
  try {
    let url = API_FUNDS;
    const {
      page,
      limit,
      orderField,
      orderDir
    } = payload;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}?page=${page + 1}&limit=${limit}`;
    }
    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }

    const response = yield call(authorizedRequest, url);
    yield put(fundFetchSuccess(response));
    yield put(refreshFundsSwitchOff());
  } catch (error) {
    yield put(fundFetchFailure(error));
    yield put(refreshFundsSwitchOff());
    ToastError(error.message);
  }
}

export const addNoteToFund = (model, modelId, note) => ({
  type: ADD_NOTE_TO_FUND.value,
  model,
  modelId,
  note,
  url: "/notes"
});
export const getFundNotes = payload => makePayload(GET_FUND_NOTES.value, payload);
export const deleteNote = payload => makePayload(DELETE_FUND_NOTES.value, payload);

export const getFundMetrics = payload => makePayload(GET_FUND_METRICS.value, payload);

// Saga all fetch
function* fetchSaga() {
  try {
    const url = API_FUNDS;
    const response = yield call(authorizedRequest, url);
    yield put(allFundFetchSuccess(response));
  } catch (error) {
    yield put(allFundFetchFailure(error));
    ToastError(error.message);
  }
}

// Saga single fetch
function* fetchSingleSaga(payload) {
  try {
    const response = yield call(authorizedRequest, `/funds/${payload.payload}`);
    yield put(fundSingleFetchSuccess(response));
    yield put(push(`/funds/${payload.payload}`));
  } catch (error) {
    yield put(fundSingleFetchFailure(error));
    ToastError(error.message);
  }
}

function* getFundInfoSaga(payload) {
  const {
    fundId
  } = payload;
  const requestSaga = subKeySaga(fundId);
  yield requestSaga(GET_FUND_INFO, `/funds/${fundId}/info`, "GET");
}

function* createSaga(action) {
  try {
    const {
      payload
    } = action;
    const response = yield call(authorizedRequest, API_FUNDS, "POST", payload);
    yield put(fundCreateSuccess(response));
    yield put(refreshFundsSwitchOn());
  } catch (error) {
    yield put(fundCreateFailure(error));
    ToastError(error.message);
  }
}

function* updateSaga(payload) {
  try {
    const fund = {
      fund: payload.payload
    };

    const response = yield call(
      authorizedRequest,
      `/funds/${payload.payload.id}`,
      "PUT",
      fund
    );
    yield put(fundUpdateSuccess(response));
    yield put(refreshFundsSwitchOn());
  } catch (error) {
    yield put(fundUpdateFailure(error));
    ToastError(error.message);
  }
}

function* fetchPaginatedDealsSaga(payload) {
  try {
    const {
      fundId,
      status,
      page,
      limit,
      orderField,
      orderDir,
      screenType
    } = payload;
    let url = `/deal/getDealsByStatusId?fund=${fundId}&statusId=${status}`;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}&page=${page + 1}&limit=${limit}`;
    }
    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }
    if (screenType) {
      url = `${url}&screenType=${screenType}`;
    }
    const response = yield call(authorizedRequest, url);
    yield put(fundDealsFetchSuccess(response, status));
  } catch (error) {
    yield put(fundDealsFetchFailure(error));
    ToastError(error.message);
  }
}

function* addNoteToFundSaga(payload) {
  const url = "/fund/addnote";
  const note = {
    model: "fund",
    modelId: payload.modelId,
    note: payload.note,
    pinned: false
  };

  yield requestSaga(ADD_NOTE_TO_FUND, url, "POST", {
    note
  });
  ToastSuccess("Note Added Successfully");
  yield getFundNotesSaga({
    modelName: "fund",
    modelId: payload.modelId,
    subKey: payload.modelId
  });
}

function* getFundNotesSaga(payload) {
  const url = `/funds/notes/${payload.modelId}`;
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_FUND_NOTES, url, "GET");
}

function* deleteNoteSaga(payload) {
  const {
    noteId,
    modelName,
    modelId
  } = payload;
  try {
    const url = `/notes/${noteId}?modelName=${modelName}&modelId=${modelId}`;
    const requestSaga = subKeySaga(payload.subKey);
    yield requestSaga(DELETE_FUND_NOTES, url, "DELETE");
    ToastSuccess("Note Deleted");
  } catch (error) {
    ToastError(`Deleting Note Failed: ${error.message}`);
  }
}

function* addFileToFundSaga(payload) {
  try {
    const response = yield call(uploadFile, payload);
    yield put(ADD_FILE_TO_FUND.createSuccess(response));
    ToastSuccess("File Uploaded Successfully");
    yield getFundFilesSaga({
      modelName: "fund",
      modelId: payload.modelId,
      subKey: payload.modelId
    });
  } catch (error) {
    yield put(ADD_FILE_TO_FUND.createFailure(error));
    ToastError(`Upload Failed: ${error.message}`);
  }
}

function* getFundFilesSaga(payload) {
  const url = "/getFiles/byModel";
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_FUND_FILES, url, "POST", payload);
}

function* deleteFundFileSaga(payload) {
  const url = `/files/${payload.fileId}`;
  yield requestSaga(DELETE_FUND_FILE, url, "DELETE");
  if (payload.shouldUpdateAfter) {
    yield getFundFilesSaga({
      modelId: payload.modelId,
      modelName: payload.modelName,
      subKey: payload.subKey
    });
  }
}

function* getFundMetricsSaga(payload) {
  const url = `/metrics/funds/${payload.fundId}`;
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_FUND_METRICS, url, "GET");
}

function* downloadFundsSaga(payload) {
  const {
    orderField,
    orderDir
  } = payload;

  let url = API_FUNDS;
  url = `${url}?shouldDownload=true`;

  // Add sorting params
  if (orderField && orderDir) {
    url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
  }
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(FUNDS_DOWNLOAD.createFailure(error));
  }
}

export function* fundSaga() {
  yield takeLatest(REFRESH_FUNDS.value, refreshFundsSwitchSaga);
  yield takeLatest(FUNDS_FETCH_REQUEST, fetchPaginatedSaga);
  yield takeLatest(ALL_FUNDS_FETCH_REQUEST, fetchSaga);
  yield takeLatest(FUND_SINGLE_FETCH_REQUEST, fetchSingleSaga);
  yield takeLatest(GET_FUND_INFO.value, getFundInfoSaga);
  yield takeLatest(GET_FUND_NAME.value, getFundNameSaga);
  yield takeLatest(FUND_CREATE_REQUEST, createSaga);
  yield takeLatest(FUND_UPDATE_REQUEST, updateSaga);
  yield takeEvery(FUND_DEALS_FETCH_REQUEST, fetchPaginatedDealsSaga);
  yield takeLatest(ADD_NOTE_TO_FUND.value, addNoteToFundSaga);
  yield takeLatest(GET_FUND_NOTES.value, getFundNotesSaga);
  yield takeLatest(DELETE_FUND_NOTES.value, deleteNoteSaga);
  yield takeLatest(ADD_FILE_TO_FUND.value, addFileToFundSaga);
  yield takeEvery(GET_FUND_FILES.value, getFundFilesSaga);
  yield takeLatest(DELETE_FUND_FILE.value, deleteFundFileSaga);
  yield takeLatest(GET_FUND_METRICS.value, getFundMetricsSaga);
  yield takeLatest(FUNDS_DOWNLOAD.value, downloadFundsSaga);
  yield takeLatest(GET_AVAILABLE_COLLAB_GROUPS.value, getAvailableCollabGroupsSaga);
}
